import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
export { SD_INVOICE_CONFIG, type SDInvoiceMailData } from '../payment/sd-invoice-config.js';
import { SDInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
export { SDInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
import { Address } from '@adornis/address/db/address.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { RegistrationStammdatenPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { validate } from '@adornis/validation/decorators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { isSDInternMail } from '../payment/sd-email.js';

export const sdRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::sd',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new SDStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(SDStammdaten.fields).filter(it => !['_id', '_class', 'userID', 'institutionID'].includes(it));
  },
  classType: () => SDStammdaten,
};

export enum SDSalutationEnum {
  SDFRAU = 'SDFRAU',
  SDHERR = 'SDHERR',
  SDSONSTIGES = 'SDSONSTIGES',
}
registerEnum({ enum: SDSalutationEnum, name: 'SDSalutationEnum' });

export enum SDInstitutionEnum {
  SDRECHT = 'SDRECHT',
  SDWIRTSCHAFT = 'SDWIRTSCHAFT',
  SDGESELLSCHAFT = 'SDGESELLSCHAFT',
  SDERZIEHUNG = 'SDERZIEHUNG',
  SDPSYCHOLOGIE = 'SDPSYCHOLOGIE',
  SDEVANG_THEOLOGIE = 'SDEVANG_THEOLOGIE',
  SDKATH_THEOLOGIE = 'SDKATH_THEOLOGIE',
  SDPHILOSOPHIE = 'SDPHILOSOPHIE',
  SDSPRACHEN = 'SDSPRACHEN',
  SDNEU_PHILOLOGIE = 'SDNEU_PHILOLOGIE',
  SDGEO = 'SDGEO',
  SDINFORMATIK = 'SDINFORMATIK',
  SDPHYSIK = 'SDPHYSIK',
  SDCHEMIE = 'SDCHEMIE',
  SDBIOLOGIE = 'SDBIOLOGIE',
  SDMEDIZIN = 'SDMEDIZIN',
  SDUNIKLINIKUM = 'SDUNIKLINIKUM',
  SDEXTERN = 'SDEXTERN',
  SDZENTRALE = 'SDZENTRALE',
  SDOTHERS = 'SDOTHERS',
}
registerEnum({ enum: SDInstitutionEnum, name: 'SDInstitutionEnum' });

export enum SDOccupationEnum {
  SDHIWI_GOETHE = 'SDHIWI_GOETHE',
  SDSTUDEMPLOYEE_GOETHE = 'SDSTUDEMPLOYEE_GOETHE',
  SDUB_GOETHE = 'SDUB_GOETHE',
  SDEMPLOYEE_GOETHE = 'SDEMPLOYEE_GOETHE',
  SDTEACHER = 'SDTEACHER',
  SDHESS_HOCHSCHULANGEHOERIGE = 'SDHESS_HOCHSCHULANGEHOERIGE',
  SDSONSTIGE = 'SDSONSTIGE',
}
registerEnum({ enum: SDOccupationEnum, name: 'SDOccupationEnum' });

export const DFN_DEPARTMENT_CODE_TO_SD_INSTITUTION: Record<string, SDInstitutionEnum> = {
  '01': SDInstitutionEnum.SDRECHT,
  '02': SDInstitutionEnum.SDWIRTSCHAFT,
  '03': SDInstitutionEnum.SDGESELLSCHAFT,
  '04': SDInstitutionEnum.SDERZIEHUNG,
  '05': SDInstitutionEnum.SDPSYCHOLOGIE,
  '06': SDInstitutionEnum.SDEVANG_THEOLOGIE,
  '07': SDInstitutionEnum.SDKATH_THEOLOGIE,
  '08': SDInstitutionEnum.SDPHILOSOPHIE,
  '09': SDInstitutionEnum.SDSPRACHEN,
  '10': SDInstitutionEnum.SDNEU_PHILOLOGIE,
  '11': SDInstitutionEnum.SDGEO,
  '12': SDInstitutionEnum.SDINFORMATIK,
  '13': SDInstitutionEnum.SDPHYSIK,
  '14': SDInstitutionEnum.SDCHEMIE,
  '15': SDInstitutionEnum.SDBIOLOGIE,
  '16': SDInstitutionEnum.SDMEDIZIN,
};

export const SDInternOccupations = [
  SDOccupationEnum.SDHIWI_GOETHE,
  SDOccupationEnum.SDSTUDEMPLOYEE_GOETHE,
  // SDOccupationEnum.SDTUTOR_GOETHE,
  SDOccupationEnum.SDUB_GOETHE,
  SDOccupationEnum.SDEMPLOYEE_GOETHE,
];

export const SDHessenOccupations = [SDOccupationEnum.SDHESS_HOCHSCHULANGEHOERIGE, SDOccupationEnum.SDTEACHER];
registerEnum({ enum: SDInvoiceReceiverEnum, name: 'SDInvoiceReceiverEnum' });

@Entity()
export class SDStammdaten extends UserStammdaten {
  static override _class = 'SDStammdaten';

  override getTranslationNamespace() {
    return 'sd';
  }

  @Field(type => SDSalutationEnum)
  salutation: Maybe<SDSalutationEnum>;

  @Field(type => String)
  title: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  phoneNumber: Maybe<string>;

  @Field(type => String)
  matrikelnummer: Maybe<string>;

  @validate(nonOptional())
  @Field(type => SDInstitutionEnum)
  department: Maybe<SDInstitutionEnum>;

  /**
   * only applicable for external department
   */
  @Field(type => String)
  externalDepartmentUniversity: Maybe<string>;

  /**
   * only applicable for external department
   */
  @Field(type => String)
  externalDepartmentDepartment: Maybe<string>;

  /**
   * only applicable for others department
   */
  @Field(type => String)
  otherDepartmentFurtherInformation: Maybe<string>;

  @validate(nonOptional())
  @Field(type => SDOccupationEnum)
  occupation: Maybe<SDOccupationEnum>;

  @validate(
    and(nonOptional(), async ({ value, key, target }) => {
      let user = null as Maybe<NarrowedEntity<User, { email: 1 }>>;
      try {
        user = await getUserByID(target.userID)({ email: 1 });
      } catch (err) {
        console.trace(err);
        logger.error(err);
      }

      // if no user present yet (i.e. while filling out the registration form) we cannot validate this further
      if (!user) return;

      if (
        (target.department === SDInstitutionEnum.SDMEDIZIN ||
          target.department === SDInstitutionEnum.SDUNIKLINIKUM ||
          !isSDInternMail(user.email)) &&
        value === SDInvoiceReceiverEnum.SDINTERNAL
      )
        throw new ValidationError('Invalid invoice receiver', {
          key,
          target,
          translationKey: 'invalid_invoice_receiver',
        });

      if (
        ![SDInstitutionEnum.SDMEDIZIN, SDInstitutionEnum.SDUNIKLINIKUM].includes(target.department) &&
        isSDInternMail(user.email) &&
        value === SDInvoiceReceiverEnum.SDEXTERNAL
      )
        throw new ValidationError('Invalid invoice receiver', {
          key,
          target,
          translationKey: 'invalid_invoice_receiver',
        });
    }),
  )
  @Field(type => SDInvoiceReceiverEnum, {
    dynamicValidator: 'non_optional',
    default: v => (v ?? SDInvoiceReceiverEnum.SDPRIVATE) as typeof SDInvoiceReceiverEnum,
  })
  invoiceReceiver: Maybe<SDInvoiceReceiverEnum>;

  /**
   * only applicable for business invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (
      target.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL ||
      target.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL
    )
      return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  institution: Maybe<string>;

  /**
   * only applicable for business invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (
      target.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL ||
      target.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL
    )
      return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  division: Maybe<string>;

  /**
   * only applicable for business invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (
      target.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL ||
      target.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL
    )
      return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  postbox: Maybe<string>;

  @Field(type => String)
  attention: Maybe<string>;

  @validate(({ value, target, key }) => {
    const errorFields = (
      !target.address
        ? [{ path: ['address'], target }]
        : [
            !target.address?.streetAndHousenumber
              ? { path: ['address', 'streetAndHousenumber'], target: target.address }
              : null,
            ...(!target.address.zipCity
              ? [{ path: ['address', 'zipCity'], target: target.address }]
              : [
                  !target.address?.zipCity?.zip
                    ? { path: ['address', 'zipCity', 'zip'], target: target.address?.zipCity }
                    : null,
                  !target.address?.zipCity?.city
                    ? { path: ['address', 'zipCity', 'city'], target: target.address?.zipCity }
                    : null,
                ]),
          ]
    ).filter(Boolean) as Array<{ path: string[]; target: any }>;

    if (errorFields.length)
      throw new MultipleValidationError(
        errorFields.map(
          ({ path, target }) =>
            new ValidationError(`Key ${path.slice(-1)[0]} is required!`, {
              key: path.slice(-1)[0] as any,
              translationKey: 'validation_non_optional',
              path: path.slice(0, -1),
              target,
            }),
        ),
      );
  })
  @Field(type => Address, { default: v => v ?? new Address({}) })
  address: Maybe<Address>;
}
